import {Brick, registerBrick, BrickContext} from 'olympe';
import ReactDOM from 'react-dom';
import React from 'react';
import {getIcon} from '../shared/utils/_icons.jsx';
import Checkbox from '@mui/material/Checkbox';
import makeStyles from '@mui/styles/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import {PropTypes} from 'prop-types';
import Button from '@mui/material/Button';
import {combineProps, createEvents} from '../shared/_helpers';


/**
 * Component that is used on the Monitor tab of the Risk & Crisis App
 */
export default class MonitorTable extends Brick {
    /**
     * This method runs when the brick is ready in the HTML DOM.
     * @override
     * @param {!BrickContext} context
     * @param {!Element} elementDom
     */
    draw(context, elementDom) {
        const events = createEvents([
            {
                name: 'onSave',
                event: context.getEvent('On Save'),
                before: () => {
                },
            },
        ]);

        combineProps([
            {name: 'geoData', prop: context.getProperty('Geo data'), defaultValue: ''},
            {name: 'riskData', prop: context.getProperty('Risk data'), defaultValue: ''},
            {name: 'vendorData', prop: context.getProperty('Vendor data'), defaultValue: ''},
            {name: 'materialData', prop: context.getProperty('Material data'), defaultValue: ''},
            {name: 'otherData', prop: context.getProperty('Other data'), defaultValue: ''},
            {name: 'selectedItems', prop: context.getProperty('Selected items'), defaultValue: ''},
            {name: 'savedData', prop: context.getProperty('Saved data'), defaultValue: ''},
        ]).subscribe((props) => {
            ReactDOM.render(
                (<MonitorTable.Component
                    {...props}
                    {...events}
                    context={context}
                />), elementDom);
        });
    }
}

/**
 *
 * @param {Object} props
 * @param {string} props.geoData
 * @param {string} props.riskData
 * @param {string} props.vendorData
 * @param {string} props.materialData
 * @param {string} props.otherData
 * @param {string} props.selectedItems
 * @param {string} props.savedData
 * @param {function(*)} props.onSave
 * @return {JSX.Element}
 * @constructor
 */
MonitorTable.Component = (props) => {
    const materials = JSON.parse(props.materialData);
    const vendors = JSON.parse(props.vendorData);
    const risks = JSON.parse(props.riskData);
    const geos = JSON.parse(props.geoData);
    const other = JSON.parse(props.otherData);

    const riskTypes = ['Geo', 'Financial', 'Sourcing flexibility', 'Supplier OTIF', 'Planning', 'Quality incident', 'Cyber', 'Other'];
    const statusList = [{value: 'Mitigation/BCP Ready', color: '#64A80A'},
        {value: 'Mitigation/BCP Identified', color: '#F19100'},
        {value: 'Mitigation/BCP Being identified', color: '#D00032'}];
    const hypercareOptions = ['Yes', 'No'];


    const getIconFromRisk = (risk) => {
        switch (risk) {
        case 'Geo':
            return 'LocationOnOutlined';
        case 'Financial':
            return 'AttachMoney';
        case 'Sourcing flexibility':
            return 'Search';
        case 'Supplier OTIF':
            return 'PersonOutline';
        case 'Planning':
            return 'DescriptionOutlined';
        case 'Quality incident':
            return 'CancelOutlined';
        case 'Cyber':
            return 'PhonelinkLock';
        case 'Other':
            return 'ReportProblemOutlined';
        }
    };

    const getData = (object, property) => {
        if (object[property] !== undefined) {
            return object[property];
        } else {
            return '-';
        }
    };

    const riskBubbleBgColor = (risk) => {
        if (risk === 'Low') {
            return '#64A80A';
        } else if (risk === 'Medium') {
            return '#F19100';
        } else if (risk === 'High') {
            return '#D00032';
        }
    };

    const getRiskBubble = (risk, idx) => {
        const result = [];

        const risks = [];

        risks.push({icon: 'LocationOnOutlined', value: risk['Risk_Geo']});
        risks.push({icon: 'AttachMoney', value: risk['Risk_Financial']});
        risks.push({icon: 'Search', value: risk['Risk_SourcingFlex']});
        risks.push({icon: 'PersonOutline', value: risk['Risk_SupplierOTIF']});
        risks.push({icon: 'DescriptionOutlined', value: risk['Risk_Planning']});
        risks.push({icon: 'CancelOutlined', value: risk['Risk_QualityIncident']});
        risks.push({icon: 'PhonelinkLock', value: risk['Risk_Cyber']});

        result.push((<div key={'rcMonitorTableRiskDataRiskBubblesIdx' + idx} className={'rc-table-risk-type-and-level'}>
            {risks.map((r, index) => {
                if (r.value) {
                    return (<div key={'rcMonitorTableRiskDataRiskBubbleIndex' + index} className={'rc-table-risk-bubble'} style={{backgroundColor: riskBubbleBgColor(r.value)}}>
                        {getIcon(r.icon, true, {style: {fontSize: '16px'}})}
                    </div>);
                }
            })}
        </div>));
        return result;
    };

    const icon = getIcon('CheckBoxOutlineBlank', true, {style: {fontSize: '1.25rem'}});
    const checkedIcon = getIcon('CheckBox', true, {style: {fontSize: '1.25rem'}});

    const useStyles = makeStyles ( () => ({
        root: {
            'color': '#656F71',
            '&$checked': {
                color: '#64A80A',
            },
        },
        checked: {},
        formControl: {
            minWidth: 120,
        },
    }));

    const classes = useStyles();

    const [expanded, setExpanded] = React.useState(false);
    const [anchorElRiskCrisisElement, setAnchorElRiskCrisisElement] = React.useState(null);
    const [riskCrisisElement, setRiskCrisisElement] = React.useState(other[0].Risk_Crisis_Element);

    const [anchorElStatus, setAnchorElStatus] = React.useState(null);
    const [status, setStatus] = React.useState(statusList.find((status) => status.value === other[0].Status));

    const [anchorElHypercare, setAnchorElHypercare] = React.useState(null);
    const [hypercare, setHypercare] = React.useState(other[0].Hypercare);

    const [action, setAction] = React.useState(other[0].Action);
    const [isActionEditable, setIsActionEditable] = React.useState(true);

    const [crisisDetail, setCrisisDetail] = React.useState(other[0].Crisis_Detail);
    const [isCrisisDetailEditable, setIsCrisisDetailEditable] = React.useState(true);

    const handleClickRiskCrisisElement = (event) => {
        setAnchorElRiskCrisisElement(event.currentTarget);
    };

    const handleCloseRiskCrisis = () => {
        setAnchorElRiskCrisisElement(null);
    };

    const handleClickStatus = (event) => {
        setAnchorElStatus(event.currentTarget);
    };

    const handleCloseStatus = () => {
        setAnchorElStatus(null);
    };

    const handleClickHypercare = (event) => {
        setAnchorElHypercare(event.currentTarget);
    };

    const handleCloseHypercare = () => {
        setAnchorElHypercare(null);
    };

    const handleChangeAction = (event) => {
        setAction(event.target.value);
    };

    const handleClickAction = () => {
        setIsActionEditable(!isActionEditable);
    };

    const handleChangeCrisisDetail = (event) => {
        setCrisisDetail(event.target.value);
    };

    const handleClickCrisisDetail = () => {
        setIsCrisisDetailEditable(!isCrisisDetailEditable);
    };

    const handleClickSave = () => {
        const data = {
            Action: action,
            Crisis_Detail: crisisDetail,
            Hypercare: hypercare,
            Risk_Crisis_Element: riskCrisisElement,
            Status: status.value,
            Modified_By: other[0].Modified_By,
        };

        props.context.getProperty('Saved data').set(JSON.stringify(data));
        props.onSave();
    };

    return (
        <div className={'rc-monitor'}>
            <div className={'rc-monitor-table-gradient'} style={{display: expanded ? 'none' : ''}}/>

            <div className={'rc-monitor-table'} style={expanded ? {} : {height: '265px', overflow: 'hidden'}}>

                <div className={'rc-monitor-table-command-card'}>
                    <Checkbox
                        classes={{
                            root: classes.root,
                            checked: classes.checked,
                        }}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{width: '30px'}}
                    />
                    <div className={'rc-monitor-table-expand-bubble'} onClick={() => setExpanded(!expanded)}>
                        {getIcon(expanded ? 'ExpandLess' : 'ExpandMore', true, {style: {fontSize: '16px'}})}
                    </div>
                </div>
                <div className={'rc-monitor-table-data-card'} style={{marginLeft: '12px'}}>
                    <div className={'rc-monitor-table-data-card-title'}>
                        Material Data
                    </div>

                    <div className={'rc-monitor-table-data-card-horizontal-separator'}/>

                    <div className={'rc-monitor-table-data-card-informations'}>
                        <div className={'rc-monitor-table-data-card-subtitle'}>Material</div>
                        {materials.map((material, idx) => {
                            return (
                                <div key={'rcMonitorTableMaterialDataMaterialIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{material.Id + ' - ' + material.Name}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Spend Category L1</div>
                        {materials.map((material, idx) => {
                            return (
                                <div key={'rcMonitorTableMaterialDataCatL1Idx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(material, 'Spend_Cat_L1')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Spend Category L2</div>
                        {materials.map((material, idx) => {
                            return (
                                <div key={'rcMonitorTableMaterialDataCatL2Idx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(material, 'Spend_Cat_L2')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Spend Category L3</div>
                        {materials.map((material, idx) => {
                            return (
                                <div key={'rcMonitorTableMaterialDataCatL3Idx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(material, 'Spend_Cat_L3')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Specification</div>
                        {materials.map((material, idx) => {
                            return (
                                <div key={'rcMonitorTableMaterialDataSpecIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{material.Specification.Id + ' - ' + material.Specification.Name}</div>
                            );
                        })}
                    </div>
                </div>
                <div className={'rc-monitor-table-data-card'}>
                    <div className={'rc-monitor-table-data-card-title'}>
                        Vendor Data
                    </div>

                    <div className={'rc-monitor-table-data-card-horizontal-separator'}/>

                    <div className={'rc-monitor-table-data-card-informations'}>
                        <div className={'rc-monitor-table-data-card-subtitle'}>Parent vendor</div>
                        {vendors.map((vendor, idx) => {
                            return (
                                <div key={'rcMonitorTableVendorDataParentVendorIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(vendor, 'Parent_Vendor')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Main vendor</div>
                        {vendors.map((vendor, idx) => {
                            return (
                                <div key={'rcMonitorTableVendorDataMainVendorIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{vendor.Main_Vendor.Id + ' - ' + vendor.Main_Vendor.Name}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Vendor site</div>
                        {vendors.map((vendor, idx) => {
                            return (
                                <div key={'rcMonitorTableVendorDataVendorSiteIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{vendor.Vendor_Site.Id + ' - ' + vendor.Vendor_Site.Name}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-double-columns'}>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>VS city</div>
                                {vendors.map((vendor, idx) => {
                                    return (
                                        <div key={'rcMonitorTableVendorDataVSCityIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(vendor.Vendor_Site, 'City')}</div>
                                    );
                                })}
                            </div>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>VS Country</div>
                                {vendors.map((vendor, idx) => {
                                    return (
                                        <div key={'rcMonitorTableVendorDataVSCountryIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(vendor.Vendor_Site, 'Country')}</div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={'rc-monitor-table-data-card'}>
                    <div className={'rc-monitor-table-data-card-title'}>
                        Risk Data
                    </div>

                    <div className={'rc-monitor-table-data-card-horizontal-separator'}/>

                    <div className={'rc-monitor-table-data-card-informations'}>
                        <div className={'rc-monitor-table-data-card-subtitle'}>Financial rating</div>
                        {risks.map((risk, idx) => {
                            return (
                                <div key={'rcMonitorTableRiskDataFinancialRatingIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(risk, 'Financial_Rating')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>BCP status</div>
                        {risks.map((risk, idx) => {
                            return (
                                <div key={'rcMonitorTableRiskDataBCPStatusIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(risk, 'BCP_Status')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Origin of sourcing</div>
                        {risks.map((risk, idx) => {
                            return (
                                <div key={'rcMonitorTableRiskDataSourcingOriginIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(risk, 'Origin_Of_Sourcing')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-double-columns'}>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>Stock</div>
                                {risks.map((risk, idx) => {
                                    return (
                                        <div key={'rcMonitorTableRiskDataStockIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(risk, 'Stock')}</div>
                                    );
                                })}
                            </div>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>Stock cover</div>
                                {risks.map((risk, idx) => {
                                    return (
                                        <div key={'rcMonitorTableRiskDataStockCoverIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(risk, 'Stock_Cover')}</div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={'rc-monitor-table-data-card-subtitle'} style={{marginBottom: '10px'}}>Risk type and level</div>
                        {risks.map((risk, idx) => {
                            return getRiskBubble(risk, idx);
                        })}
                    </div>
                </div>
                <div className={'rc-monitor-table-data-card'}>
                    <div className={'rc-monitor-table-data-card-title'}>
                        Geo Data
                    </div>

                    <div className={'rc-monitor-table-data-card-horizontal-separator'}/>

                    <div className={'rc-monitor-table-data-card-informations'}>
                        <div className={'rc-monitor-table-data-card-double-columns'}>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>Zone</div>
                                {geos.map((geo, idx) => {
                                    return (
                                        <div key={'rcMonitorTableGeoDataZoneIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(geo, 'Zone')}</div>
                                    );
                                })}
                            </div>
                            <div>
                                <div className={'rc-monitor-table-data-card-subtitle'}>Market</div>
                                {geos.map((geo, idx) => {
                                    return (
                                        <div key={'rcMonitorTableGeoDataMarketIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(geo, 'Market')}</div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={'rc-monitor-table-data-card-subtitle'}>Country</div>
                        {geos.map((geo, idx) => {
                            return (
                                <div key={'rcMonitorTableGeoDataCountryIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{getData(geo, 'Country')}</div>
                            );
                        })}

                        <div className={'rc-monitor-table-data-card-subtitle'}>Plant</div>
                        {geos.map((geo, idx) => {
                            return (
                                <div key={'rcMonitorTableGeoDataPlantIdx' + idx} className={'rc-monitor-table-data-card-normal-text'}>{geo.Plant.Id + ' - ' + geo.Plant.Name}</div>
                            );
                        })}
                    </div>
                </div>
                <div className={'rc-monitor-table-data-card'} style={{marginRight: '13px', display: 'flex', flexDirection: 'column'}}>
                    <div className={'rc-monitor-table-data-card-title'} style={{marginBottom: '0'}}>
                        Other Data
                    </div>

                    <div className={'rc-monitor-table-data-card-normal-text'} style={{fontStyle: 'italic', marginLeft: '15px', marginBottom: '10px'}}>
                        {'modified by @' + other[0].Modified_By.Name + ' on ' + other[0].Modified_By.Date}
                    </div>

                    <div className={'rc-monitor-table-data-card-horizontal-separator'}/>

                    <div className={'rc-monitor-table-data-card-other-data-element'}>
                        <div className={'rc-monitor-table-data-card-other-data-element-subtitle'}>Risk/Crisis Element</div>
                        <div className={'rc-monitor-table-data-card-other-data-element-text'}>
                            {getIcon(getIconFromRisk(riskCrisisElement), true, {style: {fontSize: '14px', marginRight: '6px'}})}
                            {riskCrisisElement}
                        </div>
                        <Menu
                            id="other-data-risk-crisis-menu"
                            color="primary"
                            anchorEl={anchorElRiskCrisisElement}
                            keepMounted
                            open={Boolean(anchorElRiskCrisisElement)}
                            onClose={handleCloseRiskCrisis}
                        >
                            {riskTypes.map((risk) => {
                                return (
                                    <MenuItem onClick={() => {
                                        setRiskCrisisElement(risk);
                                        handleCloseRiskCrisis();
                                    }}
                                    key={'OtherDataRiskCrisis' + risk}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#F1910044'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#00000000'}
                                    >
                                        {getIcon(getIconFromRisk(risk), true, {style: {fontSize: '15px', marginRight: '6px'}})}
                                        {risk}
                                    </MenuItem>
                                );
                            } )}
                        </Menu>
                        <div onClick={handleClickRiskCrisisElement} style={{cursor: 'pointer'}}>
                            {getIcon('ExpandMore', true, {style: {fontSize: '15px', marginLeft: 'auto'}})}
                        </div>

                    </div>

                    <div className={'rc-monitor-table-data-card-other-data-element'}>
                        <div className={'rc-monitor-table-data-card-other-data-element-subtitle'}>Status</div>
                        <div className={'rc-monitor-table-data-card-other-data-element-text'}>
                            <div className={'rc-monitor-table-data-card-other-data-status-circle'} style={{backgroundColor: status.color}}/>
                            {status.value}
                        </div>
                        <Menu
                            id="other-data-status-menu"
                            color="primary"
                            anchorEl={anchorElStatus}
                            keepMounted
                            open={Boolean(anchorElStatus)}
                            onClose={handleCloseStatus}
                        >
                            {statusList.map((status) => {
                                return (
                                    <MenuItem onClick={() => {
                                        setStatus(status);
                                        handleCloseStatus();
                                    }}
                                    key={'OtherDataStatus' + status.value}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#F1910044'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#00000000'}
                                    >
                                        <div className={'rc-monitor-table-data-card-other-data-status-circle'} style={{backgroundColor: status.color}}/>
                                        {status.value}
                                    </MenuItem>
                                );
                            } )}
                        </Menu>
                        <div onClick={handleClickStatus} style={{cursor: 'pointer'}}>
                            {getIcon('ExpandMore', true, {style: {fontSize: '15px', marginLeft: 'auto'}})}
                        </div>
                    </div>

                    <div className={'rc-monitor-table-data-card-other-data-element'}>
                        <div className={'rc-monitor-table-data-card-other-data-element-subtitle'}>Action</div>
                        <div className={'rc-monitor-table-data-card-other-data-element-text'}>
                            <input
                                className={'rc-monitor-table-data-card-other-data-element-text'}
                                type='text'
                                style={{border: '0', outline: 'none', width: '95%'}}
                                readOnly={isActionEditable}
                                value={action}
                                onChange={handleChangeAction} />
                        </div>
                        <div onClick={handleClickAction} style={{cursor: 'pointer'}}>
                            {getIcon('Edit', true, {style: {fontSize: '14px', marginLeft: 'auto'}})}
                        </div>
                    </div>

                    <div className={'rc-monitor-table-data-card-other-data-element'}>
                        <div className={'rc-monitor-table-data-card-other-data-element-subtitle'}>Hypercare</div>
                        <div className={'rc-monitor-table-data-card-other-data-element-text'}>
                            {hypercare}
                        </div>
                        <Menu
                            id="other-data-hypercare-menu"
                            color="primary"
                            anchorEl={anchorElHypercare}
                            keepMounted
                            open={Boolean(anchorElHypercare)}
                            onClose={handleCloseHypercare}
                        >
                            {hypercareOptions.map((h) => {
                                return (
                                    <MenuItem onClick={() => {
                                        setHypercare(h);
                                        handleCloseHypercare();
                                    }}
                                    key={'OtherDataHypercare' + h}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#F1910044'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#00000000'}
                                    >
                                        {h}
                                    </MenuItem>
                                );
                            } )}
                        </Menu>
                        <div onClick={handleClickHypercare} style={{cursor: 'pointer'}}>
                            {getIcon('ExpandMore', true, {style: {fontSize: '15px', marginLeft: 'auto'}})}
                        </div>
                    </div>

                    <div className={'rc-monitor-table-data-card-other-data-element'}>
                        <div className={'rc-monitor-table-data-card-other-data-element-subtitle'}>Crisis detail</div>
                        <div className={'rc-monitor-table-data-card-other-data-element-text'}>
                            <input
                                className={'rc-monitor-table-data-card-other-data-element-text'}
                                type='text'
                                style={{border: '0', outline: 'none', width: '95%'}}
                                readOnly={isCrisisDetailEditable}
                                value={crisisDetail}
                                onChange={handleChangeCrisisDetail} />
                        </div>
                        <div onClick={handleClickCrisisDetail} style={{cursor: 'pointer'}}>
                            {getIcon('Edit', true, {style: {fontSize: '14px', marginLeft: 'auto'}})}
                        </div>
                    </div>

                    <div className={'rc-monitor-table-data-card-other-data-save-button'} style={{display: expanded ? '' : 'none'}}>
                        <Button
                            onClick={handleClickSave}
                            variant="contained"
                            size="small"
                            style={{backgroundColor: '#656F71',
                                color: 'white',
                                textTransform: 'capitalize'}}
                            startIcon={getIcon('Save')}
                        >Save</Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

MonitorTable.Component.propTypes = {
    geoData: PropTypes.string,
    riskData: PropTypes.string,
    vendorData: PropTypes.string,
    materialData: PropTypes.string,
    otherData: PropTypes.string,
    selectedItems: PropTypes.string,
    savedData: PropTypes.string,
    onSave: PropTypes.func,
    context: PropTypes.object,
};

registerBrick('017b3f3ee9922455f437', MonitorTable);
